import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle2, Circle, Play, Pause, ExternalLink, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { format } from 'date-fns';
import TaskDetailModal from '../components/TaskDetailModal';
import { queueTaskEditHandoff } from '../utils/taskEditHandoff';
import { useNavigate } from 'react-router-dom';
import { useTaskOptions, getOptionColor, optionBadgeStyle } from '../utils/taskOptions';
import { TaskIdBadge } from '../components/TaskIdBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Curated motivational quotes for daily inspiration
const MOTIVATIONAL_QUOTES = [
  { text: "The secret of getting ahead is getting started.", author: "Mark Twain" },
  { text: "Don't watch the clock; do what it does. Keep going.", author: "Sam Levenson" },
  { text: "The future depends on what you do today.", author: "Mahatma Gandhi" },
  { text: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill" },
  { text: "Believe you can and you're halfway there.", author: "Theodore Roosevelt" },
  { text: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
  { text: "Excellence is not a skill, it's an attitude.", author: "Ralph Marston" },
  { text: "Your limitation—it's only your imagination.", author: "Anonymous" },
  { text: "Push yourself, because no one else is going to do it for you.", author: "Anonymous" },
  { text: "Great things never come from comfort zones.", author: "Anonymous" },
  { text: "Dream it. Wish it. Do it.", author: "Anonymous" },
  { text: "Success doesn't just find you. You have to go out and get it.", author: "Anonymous" },
  { text: "The harder you work for something, the greater you'll feel when you achieve it.", author: "Anonymous" },
  { text: "Dream bigger. Do bigger.", author: "Anonymous" },
  { text: "Don't stop when you're tired. Stop when you're done.", author: "Anonymous" },
  { text: "Wake up with determination. Go to bed with satisfaction.", author: "Anonymous" },
  { text: "Do something today that your future self will thank you for.", author: "Sean Patrick Flanery" },
  { text: "Little things make big days.", author: "Anonymous" },
  { text: "It's going to be hard, but hard does not mean impossible.", author: "Anonymous" },
  { text: "Don't wait for opportunity. Create it.", author: "Anonymous" },
  { text: "Sometimes we're tested not to show our weaknesses, but to discover our strengths.", author: "Anonymous" },
  { text: "The key to success is to focus on goals, not obstacles.", author: "Anonymous" },
  { text: "Dream it. Believe it. Build it.", author: "Anonymous" },
  { text: "Your only limit is you.", author: "Anonymous" },
  { text: "Sometimes later becomes never. Do it now.", author: "Anonymous" },
  { text: "One day or day one. You decide.", author: "Anonymous" },
  { text: "It's not about perfect. It's about effort.", author: "Jillian Michaels" },
  { text: "You don't have to be great to start, but you have to start to be great.", author: "Zig Ziglar" },
  { text: "A journey of a thousand miles begins with a single step.", author: "Lao Tzu" },
  { text: "The best time to plant a tree was 20 years ago. The second best time is now.", author: "Chinese Proverb" }
];

const MyDay = () => {
  const { token, user } = useAuth();
  const { RAW: TASK_OPTIONS_RAW } = useTaskOptions();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [dailyQuote, setDailyQuote] = useState(null);

  useEffect(() => {
    loadMyDayTasks();
    loadUsers();
    setDailyQuote(getDailyQuote());
  }, []);

  const getDailyQuote = () => {
    // Use current date to get a consistent quote for the day
    const today = new Date();
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
    const quoteIndex = dayOfYear % MOTIVATIONAL_QUOTES.length;
    return MOTIVATIONAL_QUOTES[quoteIndex];
  };

  const loadMyDayTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks/my-day`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await axios.put(
        `${API}/tasks/${taskId}`,
        { your_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Task updated!');
      loadMyDayTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'Completed') return <CheckCircle2 className="text-green-600" size={24} />;
    if (status === 'In Process') return <Play className="text-blue-600" size={24} />;
    if (status === 'On Hold') return <Pause className="text-orange-600" size={24} />;
    return <Circle className="text-slate-400" size={24} />;
  };

  const getCategoryColor = (category) => {
    // Prefer admin-configured colour from Task Options (single source of truth).
    const hex = getOptionColor(TASK_OPTIONS_RAW?.categories, category);
    if (hex) return { style: optionBadgeStyle(hex), className: '' };
    const colors = {
      'Technical SEO': 'bg-purple-100 text-purple-700',
      'On-Page': 'bg-blue-100 text-blue-700',
      'Off-Page': 'bg-pink-100 text-pink-700',
      'UI/UX Design': 'bg-indigo-100 text-indigo-700',
      'SEO': 'bg-green-100 text-green-700',
      'QA/Technical': 'bg-orange-100 text-orange-700',
      'Google Ads': 'bg-red-100 text-red-700',
      'Meta Ads': 'bg-cyan-100 text-cyan-700',
      'Others': 'bg-slate-100 text-slate-700',
      // Legacy categories for backwards compatibility
      'Technical': 'bg-purple-100 text-purple-700',
      'OnPage': 'bg-blue-100 text-blue-700',
      'OffPage': 'bg-pink-100 text-pink-700'
    };
    return { className: colors[category] || colors['Others'] };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading your tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="my-day-page">
      <div className="page-header">
        <h1 className="page-title">My Day</h1>
        <p className="page-subtitle">Tasks starting today or currently active</p>
      </div>

      {/* Daily Motivational Quote */}
      {dailyQuote && (
        <div className="bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 rounded-2xl shadow-sm border border-cyan-100 p-8 relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-cyan-200/20 to-blue-200/20 rounded-full blur-3xl -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full blur-3xl -ml-24 -mb-24"></div>
          
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 btn-cta rounded-xl text-white shadow-lg">
                <Sparkles size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">Daily Inspiration</h3>
                <p className="text-sm text-slate-600">{format(new Date(), 'EEEE, MMMM dd, yyyy')}</p>
              </div>
            </div>
            
            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-white/50 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="text-6xl text-cyan-500/30 leading-none font-serif">"</div>
                <div className="flex-1 pt-2">
                  <p className="text-xl md:text-2xl font-medium text-slate-800 leading-relaxed mb-3 italic">
                    {dailyQuote.text}
                  </p>
                  <p className="text-sm font-semibold text-cyan-700">
                    — {dailyQuote.author}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tasks.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
          <CheckCircle2 className="mx-auto text-green-500 mb-4" size={64} />
          <h3 className="text-2xl font-bold text-slate-800 mb-2">All clear!</h3>
          <p className="text-slate-600">You don't have any tasks starting today or currently in progress.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tasks.map((task) => (
            <div
              key={task.id}
              data-testid={`task-card-${task.id}`}
              onClick={() => {
                setSelectedTask(task);
                setIsDetailOpen(true);
              }}
              className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="mt-1">{getStatusIcon(task.your_status)}</div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    {(() => { const c = getCategoryColor(task.category); return (
                      <span className={`text-xs px-2 py-1 rounded-md font-semibold ${c.className || ''}`} style={c.style}>
                        {task.category}
                      </span>
                    ); })()}
                    {task.developer_dependency && (
                      <span className="text-xs px-2 py-1 rounded-md font-semibold bg-orange-100 text-orange-700">
                        Dev Dependency
                      </span>
                    )}
                    {task.manager_approval_required && task.manager_approval_status === 'Pending' && (
                      <span className="text-xs px-2 py-1 rounded-md font-semibold bg-yellow-100 text-yellow-700 border border-yellow-200">
                        ⏳ Pending Approval
                      </span>
                    )}
                    {task.manager_approval_status === 'Standby' && (
                      <span className="text-xs px-2 py-1 rounded-md font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                        ⏸️ On Standby
                      </span>
                    )}
                    {task.manager_approval_status === 'Rejected' && (
                      <span className="text-xs px-2 py-1 rounded-md font-semibold bg-red-100 text-red-700 border border-red-200">
                        ❌ Rejected - Needs Revision
                      </span>
                    )}
                  </div>
                  
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    <TaskIdBadge taskId={task.task_id} className="mr-1.5 align-middle" />
                    {task.task}
                  </h3>
                  
                  {task.manager_approval_status === 'Standby' && task.manager_notes && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                      <p className="text-xs font-semibold text-amber-800 mb-1">Manager Note (Standby):</p>
                      <p className="text-sm text-amber-700">{task.manager_notes}</p>
                    </div>
                  )}
                  
                  {task.manager_approval_status === 'Rejected' && task.manager_notes && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                      <p className="text-xs font-semibold text-red-800 mb-1">Manager Feedback:</p>
                      <p className="text-sm text-red-700">{task.manager_notes}</p>
                    </div>
                  )}
                  
                  {task.output && (
                    <p className="text-sm text-slate-600 mb-3">
                      <span className="font-medium">Output:</span> {task.output}
                    </p>
                  )}
                  
                  {task.link && (
                    <a
                      href={task.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-cyan-600 hover:text-cyan-700 mb-3"
                    >
                      <ExternalLink size={14} /> View Output Link
                    </a>
                  )}
                  
                  {task.start_date && (
                    <p className="text-xs text-slate-500">
                      Started: {format(new Date(task.start_date), 'MMM dd, yyyy')}
                    </p>
                  )}
                </div>
                
                <div className="flex flex-col gap-2">
                  {task.your_status !== 'Completed' && (
                    <>
                      {task.your_status === 'Yet to start' && (
                        <Button
                          size="sm"
                          onClick={() => updateTaskStatus(task.id, 'In Process')}
                          data-testid={`start-task-${task.id}`}
                          className="bg-blue-500 hover:bg-blue-600"
                        >
                          <Play size={14} className="mr-1" /> Start
                        </Button>
                      )}
                      {task.your_status === 'In Process' && (
                        <Button
                          size="sm"
                          onClick={() => updateTaskStatus(task.id, 'Completed')}
                          data-testid={`complete-task-${task.id}`}
                          className="bg-green-500 hover:bg-green-600"
                        >
                          <CheckCircle2 size={14} className="mr-1" /> Complete
                        </Button>
                      )}
                    </>
                  )}
                  {task.your_status === 'Completed' && (
                    <span className="text-sm font-semibold text-green-600">✓ Completed</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTask}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        users={users}
        onTaskUpdated={loadMyDayTasks}
        onEdit={(task) => {
          queueTaskEditHandoff(task.id);
          setIsDetailOpen(false);
          navigate('/tasks');
        }}
      />
    </div>
  );
};

export default MyDay;